import { useEffect } from 'react'

import { useDictionaryData } from '@/components/shared/DictionaryProvider/hooks'
import { useUser } from '@/hooks/useUser'
import {
  useLazyGetCompaniesQuery,
  useLazyGetRmcCompaniesQuery,
} from '@/slices/companies'

import { getPreselectedProps, requestWrapper } from './helpers'

import type { BaseSelectAsyncProps } from '../SelectAsync'
import type { CorporateRequestParamsType, CorporateRequestType } from './types'

export const useCorporateSelectData = (
  requestParams?: CorporateRequestParamsType,
  onChange?: BaseSelectAsyncProps['onChange']
) => {
  const { dictionary } = useDictionaryData()
  const { data: userData } = useUser()

  const [
    getCompanies,
    { data: adminCompaniesData, isUninitialized: isGetCompaniesUninitialized },
  ] = useLazyGetCompaniesQuery()
  const [
    getRmcCompanies,
    { data: rmcCompaniesData, isUninitialized: isGetRmcCompaniesUninitialized },
  ] = useLazyGetRmcCompaniesQuery()

  const request = requestWrapper(
    (userData?.rmc ? getRmcCompanies : getCompanies) as CorporateRequestType,
    {
      ...requestParams,
    }
  )

  const companies = [
    ...(adminCompaniesData?.items ?? []),
    ...(rmcCompaniesData?.items ?? []),
  ]

  const isUninitialized =
    isGetCompaniesUninitialized && isGetRmcCompaniesUninitialized

  const { isDisabled, value } = getPreselectedProps(
    companies,
    request,
    isUninitialized,
    null
  )

  useEffect(() => {
    let isMounted = true

    if (isMounted && onChange && value) {
      onChange(value, { action: 'select-option', option: value })
    }

    return () => {
      isMounted = false
    }
  }, [onChange, value?.value])

  return {
    placeholder: dictionary.corporate,
    isDisabled,
    value,
    request,
  }
}
